import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Users, Clock, FileText, LogOut, UserCircle, 
  Menu, X, Calendar, ListTodo, Truck, ChevronDown, ChevronRight,
  Warehouse, ShieldAlert, Building2, TrendingUp, Bell, Mail,
  MessageSquare, Settings, Download, DollarSign, Search, User, CalendarCheck
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState, useEffect } from 'react';
import { usePWAInstall } from '@/hooks/usePWAInstall';
import InstallPrompt from '@/components/InstallPrompt';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLayout = ({ user, onLogout, onSwitchView, children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [userPermissions, setUserPermissions] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    drivers: false,
    companyHR: false,
    impact: false,
    engagement: false,
    admin: false,
    finance: false,
    warehouse: false
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);

  // PWA Install functionality
  const { isInstallable, isInstalled, promptInstall } = usePWAInstall();

  useEffect(() => {
    fetchPermissions();
    fetchNotifications();
    
    // Fetch notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    
    // Listen for notification refresh events from child components
    const handleRefreshNotifications = () => {
      fetchNotifications();
      if (showNotifications) {
        fetchNotificationDetails();
      }
    };
    window.addEventListener('refreshNotifications', handleRefreshNotifications);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('refreshNotifications', handleRefreshNotifications);
    };
  }, [showNotifications]);

  const handleInstallClick = async () => {
    if (!isInstallable) {
      // If not installable via the prompt, show platform-specific instructions
      toast.info('To install this app, use your browser\'s "Add to Home Screen" option');
      return;
    }
    
    const success = await promptInstall();
    if (success) {
      toast.success('App installed successfully!');
    }
  };

  const fetchPermissions = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/auth/permissions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUserPermissions(data);
      }
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/notifications/count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNotificationCount(data.count || 0);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const fetchNotificationDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/notifications`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications || []);
      }
    } catch (error) {
      console.error('Failed to fetch notification details:', error);
    }
  };

  const hasPermission = (permission) => {
    if (!userPermissions) return true; // Loading, show all
    if (userPermissions.permissions === 'all') return true; // Admin
    if (Array.isArray(userPermissions.permissions)) {
      return userPermissions.permissions.includes(permission);
    }
    return false;
  };

  // Search employees
  useEffect(() => {
    const searchEmployees = async () => {
      if (searchQuery.trim().length < 2) {
        setSearchResults([]);
        setShowSearchResults(false);
        return;
      }

      setSearchLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${BACKEND_URL}/api/employees`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const employees = await response.json();
          const filtered = employees.filter(emp => {
            const fullName = `${emp.first_name} ${emp.last_name}`.toLowerCase();
            const query = searchQuery.toLowerCase();
            return fullName.includes(query) || 
                   emp.email?.toLowerCase().includes(query) ||
                   emp.job_title?.toLowerCase().includes(query);
          });
          setSearchResults(filtered.slice(0, 5)); // Limit to 5 results
          setShowSearchResults(true);
        }
      } catch (error) {
        console.error('Search failed:', error);
      } finally {
        setSearchLoading(false);
      }
    };

    const debounceTimer = setTimeout(searchEmployees, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <div className="min-h-screen flex overflow-x-hidden" style={{ backgroundColor: '#F8FAFC' }}>
      {/* Persistent Sidebar - Desktop */}
      <div className="hidden lg:flex lg:flex-col lg:w-72 lg:fixed lg:inset-y-0 bg-white border-r border-gray-200 z-40">
        {/* Sidebar Header */}
        <div className="h-16 px-6 border-b border-gray-200 flex items-center">
          <img src="/Primary-Full-Colour-RGB.svg" alt="Reuseabox" className="h-7 w-auto object-contain" />
        </div>

        {/* Menu Items */}
        <div className="flex-1 overflow-y-auto py-4">
          <nav className="px-3 space-y-6">
            {/* MAIN Section */}
            <div>
              <h3 className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">MAIN</h3>
              <div className="space-y-1">
                <Link
                  to="/admin/dashboard"
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                    location.pathname === '/admin/dashboard'
                      ? 'bg-gray-100 text-gray-900 font-medium'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <LayoutDashboard className="w-5 h-5" />
                  Dashboard
                </Link>

                <Link
                  to="/admin/company-calendar"
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                    location.pathname === '/admin/company-calendar'
                      ? 'bg-gray-100 text-gray-900 font-medium'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <Calendar className="w-5 h-5" />
                  Company Calendar
                </Link>

                <Link
                  to="/admin/tasks"
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm relative ${
                    location.pathname === '/admin/tasks'
                      ? 'bg-gray-100 text-gray-900 font-medium'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <ListTodo className="w-5 h-5" />
                  Tasks
                </Link>

                {onSwitchView && (
                  <button
                    onClick={() => {
                      onSwitchView();
                      navigate('/profile');
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm text-teal-600 hover:bg-teal-50 font-medium"
                  >
                    <UserCircle className="w-5 h-5" />
                    Switch to Your Account
                  </button>
                )}
              </div>
            </div>

            {/* WORKFORCE Section */}
            {(hasPermission('employees') || hasPermission('driver_schedules') || hasPermission('submitted_logs') || hasPermission('driver_kpis') || hasPermission('warehouse_overview')) && (
              <div>
                <h3 className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">WORKFORCE</h3>
                <div className="space-y-1">
                  {hasPermission('employees') && (
                    <Link
                      to="/admin/employees"
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                        location.pathname === '/admin/employees'
                          ? 'bg-gray-100 text-gray-900 font-medium'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <Users className="w-5 h-5" />
                      {user?.role === 'admin' ? 'Employees' : 'My Team'}
                    </Link>
                  )}

                  {hasPermission('warehouse_overview') && (
                    <Link
                      to="/admin/warehouse/analytics"
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                        location.pathname === '/admin/warehouse/analytics'
                          ? 'bg-gray-100 text-gray-900 font-medium'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <TrendingUp className="w-5 h-5" />
                      Analytics
                    </Link>
                  )}

                  {hasPermission('warehouse_overview') && (
                    <Link
                      to="/admin/warehouse/timesheets"
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                        location.pathname === '/admin/warehouse/timesheets'
                          ? 'bg-gray-100 text-gray-900 font-medium'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <FileText className="w-5 h-5" />
                      Timesheets
                    </Link>
                  )}

                  {hasPermission('warehouse_overview') && (
                    <Link
                      to="/admin/toil-overview"
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                        location.pathname === '/admin/toil-overview'
                          ? 'bg-gray-100 text-gray-900 font-medium'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <Calendar className="w-5 h-5" />
                      TOIL Overview
                    </Link>
                  )}

                  {/* Year-End Review - visible to managers and admins */}
                  <Link
                    to="/admin/year-end-review"
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                      location.pathname === '/admin/year-end-review'
                        ? 'bg-gray-100 text-gray-900 font-medium'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <CalendarCheck className="w-5 h-5" />
                    Year-End Review
                  </Link>
                </div>
              </div>
            )}

            {/* OPERATIONS Section */}
            {(hasPermission('vehicles') || hasPermission('driver_schedules') || hasPermission('submitted_logs') || hasPermission('driver_kpis') || hasPermission('health_safety')) && (
              <div>
                <h3 className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">OPERATIONS</h3>
                <div className="space-y-1">
                  {hasPermission('vehicles') && (
                    <Link
                      to="/admin/vehicles"
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                        location.pathname === '/admin/vehicles'
                          ? 'bg-gray-100 text-gray-900 font-medium'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <Truck className="w-5 h-5" />
                      Vehicles
                    </Link>
                  )}

                  {hasPermission('driver_schedules') && (
                    <Link
                      to="/admin/driver-schedule"
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                        location.pathname === '/admin/driver-schedule'
                          ? 'bg-gray-100 text-gray-900 font-medium'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <Truck className="w-5 h-5" />
                      Driver Schedules
                    </Link>
                  )}

                  {hasPermission('submitted_logs') && (
                    <Link
                      to="/admin/submitted-logs"
                      data-testid="admin-menu-submitted-logs"
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                        location.pathname === '/admin/submitted-logs'
                          ? 'bg-gray-100 text-gray-900 font-medium'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <FileText className="w-5 h-5" />
                      Submitted Logs
                    </Link>
                  )}

                  {hasPermission('driver_kpis') && (
                    <Link
                      to="/admin/driver-reports"
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                        location.pathname === '/admin/driver-reports'
                          ? 'bg-gray-100 text-gray-900 font-medium'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <TrendingUp className="w-5 h-5" />
                      Driver Reports & KPIs
                    </Link>
                  )}

                  <Link
                    to="/admin/operations/timesheets"
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                      location.pathname === '/admin/operations/timesheets'
                        ? 'bg-gray-100 text-gray-900 font-medium'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <Clock className="w-5 h-5" />
                    Timesheets
                  </Link>

                  {hasPermission('health_safety') && (
                    <Link
                      to="/admin/health-safety"
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                        location.pathname === '/admin/health-safety'
                          ? 'bg-gray-100 text-gray-900 font-medium'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <ShieldAlert className="w-5 h-5" />
                      Health & Safety
                    </Link>
                  )}
                </div>
              </div>
            )}

            {/* FINANCE Section */}
            {hasPermission('finance_timesheets') && (
              <div>
                <h3 className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">FINANCE</h3>
                <div className="space-y-1">
                  <Link
                    to="/admin/warehouse-timesheets"
                    data-testid="admin-menu-warehouse-timesheets"
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                      location.pathname === '/admin/warehouse-timesheets'
                        ? 'bg-gray-100 text-gray-900 font-medium'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <DollarSign className="w-5 h-5" />
                    Warehouse Timesheets
                  </Link>
                  <Link
                    to="/admin/drivers-timesheets"
                    data-testid="admin-menu-drivers-timesheets-finance"
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                      location.pathname === '/admin/drivers-timesheets'
                        ? 'bg-gray-100 text-gray-900 font-medium'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <Truck className="w-5 h-5" />
                    Drivers Timesheets
                  </Link>
                  <Link
                    to="/admin/all-timesheets"
                    data-testid="admin-menu-all-timesheets"
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                      location.pathname === '/admin/all-timesheets'
                        ? 'bg-gray-100 text-gray-900 font-medium'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <DollarSign className="w-5 h-5" />
                    All Timesheets
                  </Link>
                </div>
              </div>
            )}

            {/* EMAILS Section */}
            <div>
              <h3 className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">EMAILS</h3>
              <div className="space-y-1">
                <Link
                  to="/admin/emails"
                  data-testid="admin-menu-emails"
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                    location.pathname === '/admin/emails'
                      ? 'bg-gray-100 text-gray-900 font-medium'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <Mail className="w-5 h-5" />
                  Task Notifications
                </Link>
              </div>
            </div>

            {/* COMPANY Section */}
            {(hasPermission('roadmap') || hasPermission('impact_stats') || hasPermission('impact_builder')) && (
              <div>
                <h3 className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">COMPANY</h3>
                <div className="space-y-1">
                  {hasPermission('roadmap') && (
                    <Link
                      to="/admin/roadmap"
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                        location.pathname === '/admin/roadmap'
                          ? 'bg-gray-100 text-gray-900 font-medium'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <Building2 className="w-5 h-5" />
                      2026 Roadmap
                    </Link>
                  )}

                  {hasPermission('impact_stats') && (
                    <Link
                      to="/admin/impact/stats"
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                        location.pathname === '/admin/impact/stats'
                          ? 'bg-gray-100 text-gray-900 font-medium'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <TrendingUp className="w-5 h-5" />
                      Update Impact Stats
                    </Link>
                  )}

                  {hasPermission('impact_builder') && (
                    <Link
                      to="/admin/impact/page"
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                        location.pathname === '/admin/impact/page'
                          ? 'bg-gray-100 text-gray-900 font-medium'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <TrendingUp className="w-5 h-5" />
                      Impact Page Builder
                    </Link>
                  )}
                </div>
              </div>
            )}

            {/* ENGAGEMENT Section */}
            {(hasPermission('announcements') || hasPermission('newsletters') || hasPermission('surveys')) && (
              <div>
                <h3 className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">ENGAGEMENT</h3>
                <div className="space-y-1">
                  {hasPermission('announcements') && (
                    <Link
                      to="/admin/announcements"
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                        location.pathname === '/admin/announcements'
                          ? 'bg-gray-100 text-gray-900 font-medium'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <MessageSquare className="w-5 h-5" />
                      Announcements
                    </Link>
                  )}

                  {hasPermission('newsletters') && (
                    <Link
                      to="/admin/newsletters"
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                        location.pathname === '/admin/newsletters'
                          ? 'bg-gray-100 text-gray-900 font-medium'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <Mail className="w-5 h-5" />
                      Newsletters
                    </Link>
                  )}

                  {hasPermission('surveys') && (
                    <Link
                      to="/admin/surveys"
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                        location.pathname === '/admin/surveys'
                          ? 'bg-gray-100 text-gray-900 font-medium'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <FileText className="w-5 h-5" />
                      Surveys
                    </Link>
                  )}
                </div>
              </div>
            )}

            {/* SYSTEM Section */}
            {hasPermission('settings') && (
              <div>
                <h3 className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">SYSTEM</h3>
                <div className="space-y-1">
                  <Link
                    to="/admin/settings/absence-types"
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                      location.pathname.startsWith('/admin/settings')
                        ? 'bg-gray-100 text-gray-900 font-medium'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <Settings className="w-5 h-5" />
                    Settings
                  </Link>
                  <Link
                    to="/admin/notification-settings"
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                      location.pathname === '/admin/notification-settings'
                        ? 'bg-gray-100 text-gray-900 font-medium'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <Bell className="w-5 h-5" />
                    Notifications
                  </Link>
                </div>
              </div>
            )}
          </nav>
        </div>

        {/* Bottom Items - User Profile */}
        <div className="border-t border-gray-200 p-3">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center text-white font-semibold text-base flex-shrink-0 overflow-hidden">
              {user?.profile_image ? (
                <img 
                  src={user.profile_image} 
                  alt={`${user.first_name} ${user.last_name}`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span>{user?.first_name?.[0]}{user?.last_name?.[0]}</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate leading-tight">
                {user?.first_name} {user?.last_name}
              </p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <p className="text-xs text-gray-500 truncate">
                  {user?.job_title || 'No Job Title'}
                </p>
                <span className="text-gray-300 text-xs">|</span>
                <button
                  onClick={onLogout}
                  className="text-xs text-gray-500 hover:text-red-600 transition-colors flex items-center gap-1"
                  title="Logout"
                >
                  <LogOut className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 lg:ml-72 flex flex-col">
        {/* Desktop Header Bar */}
        <div className="hidden lg:flex items-center justify-between sticky top-0 z-30 bg-white border-b border-gray-200 h-16 px-6">
          {/* Search Bar */}
          <div className="flex-1 max-w-xl relative">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search Workforce"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => searchQuery.length >= 2 && setShowSearchResults(true)}
                onBlur={() => setTimeout(() => setShowSearchResults(false), 200)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
              {searchQuery && (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setSearchResults([]);
                    setShowSearchResults(false);
                  }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Search Results Dropdown */}
            {showSearchResults && searchResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
                <div className="p-2">
                  {searchResults.map((employee) => (
                    <div
                      key={employee.id}
                      className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors"
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                          <User className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {employee.first_name} {employee.last_name}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {employee.job_title || 'No title'} • {employee.department || 'No department'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <button
                          onClick={() => {
                            navigate(`/admin/employees`);
                            setSearchQuery('');
                            setShowSearchResults(false);
                          }}
                          className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                        >
                          Settings
                        </button>
                        <button
                          onClick={() => {
                            navigate(`/admin/employees`);
                            setSearchQuery('');
                            setShowSearchResults(false);
                          }}
                          className="px-3 py-1.5 text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-md transition-colors"
                        >
                          View Profile
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* No Results */}
            {showSearchResults && searchQuery.length >= 2 && searchResults.length === 0 && !searchLoading && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4">
                <p className="text-sm text-gray-500 text-center">No employees found</p>
              </div>
            )}

            {/* Loading */}
            {searchLoading && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4">
                <p className="text-sm text-gray-500 text-center">Searching...</p>
              </div>
            )}
          </div>

          {/* Right Side Icons */}
          <div className="flex items-center gap-3 ml-6">
            <div className="relative">
              <button 
                onClick={() => {
                  setShowNotifications(!showNotifications);
                  if (!showNotifications) fetchNotificationDetails();
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors relative" 
                title="Notifications"
              >
                <Bell className="w-5 h-5 text-gray-600" />
                {notificationCount > 0 && (
                  <span className="absolute top-1 right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {notificationCount > 9 ? '9+' : notificationCount}
                  </span>
                )}
              </button>

              {/* Notifications Dropdown */}
              {showNotifications && (
                <>
                  <div 
                    className="fixed inset-0 z-30" 
                    onClick={() => setShowNotifications(false)}
                  />
                  <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-40 max-h-96 overflow-y-auto">
                    <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
                      <button
                        onClick={async () => {
                          try {
                            const res = await fetch(`${BACKEND_URL}/api/notifications/clear-all`, {
                              method: 'POST',
                              headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                            });
                            if (res.ok) {
                              setNotifications([]);
                              setNotificationCount(0);
                              await fetchNotifications();
                              await fetchNotificationDetails();
                            }
                          } catch (error) {
                            console.error('Failed to clear notifications:', error);
                          }
                        }}
                        disabled={notifications.length === 0}
                        className={`text-sm font-medium ${notifications.length > 0 ? 'text-teal-600 hover:text-teal-700' : 'text-gray-400 cursor-not-allowed'}`}
                      >
                        Clear All
                      </button>
                    </div>
                    {notifications.length > 0 ? (
                      <div className="divide-y divide-gray-100">
                        {notifications.map((notif) => (
                          <div key={notif.id} className="p-4 hover:bg-gray-50 transition-colors">
                            <div className="flex items-start gap-3">
                              <div className="flex-shrink-0 w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                                <ListTodo className="w-5 h-5 text-teal-600" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900">{notif.message}</p>
                                <p className="text-xs text-gray-400 mt-1">
                                  {notif.created_at && new Date(notif.created_at).toLocaleString()}
                                </p>
                                {notif.task_id && (
                                  <button
                                    onClick={() => {
                                      navigate('/admin/tasks');
                                      setShowNotifications(false);
                                    }}
                                    className="mt-2 text-sm font-medium text-teal-600 hover:text-teal-700"
                                  >
                                    View Task →
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="p-8 text-center">
                        <Bell className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-sm text-gray-500">No new notifications</p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            <button
              onClick={onLogout}
              className="ml-2 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              Logout
            </button>
          </div>
        </div>

        {/* Mobile Header */}
        <div className="lg:hidden sticky top-0 z-30 bg-white border-b border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                data-testid="admin-menu-toggle"
                onClick={() => setShowMenu(true)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Menu className="w-6 h-6 text-gray-700" />
              </button>
              <img 
                src="/Primary-Full-Colour-RGB.svg" 
                alt="Reuseabox" 
                className="h-6 w-auto object-contain"
              />
            </div>
            <button
              data-testid="admin-logout-mobile"
              onClick={onLogout}
              className="p-2 hover:bg-red-50 text-red-600 rounded-lg transition-colors"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>

      {/* Side Menu */}
      {showMenu && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/50 z-40 transition-opacity"
            onClick={() => setShowMenu(false)}
            data-testid="admin-menu-backdrop"
          />
          
          {/* Menu Drawer */}
          <div className="fixed top-0 left-0 h-full w-72 bg-white shadow-2xl z-50 transform transition-transform overflow-y-auto" data-testid="admin-side-menu">
            <div className="flex flex-col min-h-full">
              {/* Sidebar Header */}
              <div className="h-16 px-6 border-b border-gray-200 flex items-center justify-between">
                <img src="/Primary-Full-Colour-RGB.svg" alt="Reuseabox" className="h-7 w-auto object-contain" />
                <button
                  data-testid="admin-menu-close-button"
                  onClick={() => setShowMenu(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              {/* Menu Items */}
              <div className="flex-1 overflow-y-auto py-4">
                <nav className="px-3 space-y-6">
                  {/* MAIN Section */}
                  <div>
                    <h3 className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">MAIN</h3>
                    <div className="space-y-1">
                      <Link
                        to="/admin/dashboard"
                        data-testid="admin-menu-dashboard"
                        onClick={() => setShowMenu(false)}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                          location.pathname === '/admin/dashboard'
                            ? 'bg-gray-100 text-gray-900 font-medium'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                      >
                        <LayoutDashboard className="w-5 h-5" />
                        Dashboard
                      </Link>

                      <Link
                        to="/admin/company-calendar"
                        data-testid="admin-menu-company-calendar"
                        onClick={() => setShowMenu(false)}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                          location.pathname === '/admin/company-calendar'
                            ? 'bg-gray-100 text-gray-900 font-medium'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                      >
                        <Calendar className="w-5 h-5" />
                        Company Calendar
                      </Link>

                      <Link
                        to="/admin/tasks"
                        data-testid="admin-menu-tasks"
                        onClick={() => setShowMenu(false)}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm relative ${
                          location.pathname === '/admin/tasks'
                            ? 'bg-gray-100 text-gray-900 font-medium'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                      >
                        <ListTodo className="w-5 h-5" />
                        Tasks
                      </Link>

                      {onSwitchView && (
                        <button
                          data-testid="admin-menu-switch-account"
                          onClick={() => {
                            onSwitchView();
                            navigate('/profile');
                            setShowMenu(false);
                          }}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm text-teal-600 hover:bg-teal-50 font-medium"
                        >
                          <UserCircle className="w-5 h-5" />
                          Switch to Your Account
                        </button>
                      )}
                    </div>
                  </div>

                  {hasPermission('vehicles') && (
                    <Link
                      to="/admin/vehicles"
                      data-testid="admin-menu-vehicles"
                      onClick={() => setShowMenu(false)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors font-medium ${
                        location.pathname === '/admin/vehicles'
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'text-gray-700 hover:bg-emerald-50 hover:text-emerald-700'
                      }`}
                    >
                      <Truck className="w-5 h-5" />
                      Vehicles
                    </Link>
                  )}

                  {/* Drivers Overview - Collapsible */}
                  {(hasPermission('driver_schedules') || hasPermission('submitted_logs') || hasPermission('driver_kpis')) && (
                    <div>
                      <button
                        data-testid="admin-menu-drivers-toggle"
                        onClick={() => toggleSection('drivers')}
                        className="w-full flex items-center justify-between px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors font-medium"
                      >
                        <div className="flex items-center gap-3">
                          <Truck className="w-5 h-5" />
                          Drivers Overview
                        </div>
                        {expandedSections.drivers ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </button>
                      {expandedSections.drivers && (
                        <div className="ml-8 mt-1 space-y-1">
                          {hasPermission('driver_schedules') && (
                            <Link
                              to="/admin/driver-schedule"
                              data-testid="admin-menu-drivers-schedule"
                              onClick={() => setShowMenu(false)}
                              className={`w-full flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-colors ${
                                location.pathname === '/admin/driver-schedule'
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                              }`}
                            >
                              Create/View a Schedule
                            </Link>
                          )}
                          {hasPermission('submitted_logs') && (
                            <Link
                              to="/admin/submitted-logs"
                              data-testid="admin-menu-drivers-logs"
                              onClick={() => setShowMenu(false)}
                              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-800 rounded-lg transition-colors"
                            >
                              Submitted Logs
                            </Link>
                          )}
                          {hasPermission('driver_kpis') && (
                            <Link
                              to="/admin/driver-reports"
                              data-testid="admin-menu-drivers-reports"
                              onClick={() => setShowMenu(false)}
                              className={`w-full flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-colors ${
                                location.pathname === '/admin/driver-reports'
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                              }`}
                            >
                              Reports & KPIs
                            </Link>
                          )}
                          <Link
                            to="/admin/operations/timesheets"
                            data-testid="admin-menu-drivers-timesheets"
                            onClick={() => setShowMenu(false)}
                            className={`w-full flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-colors ${
                              location.pathname === '/admin/operations/timesheets'
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                            }`}
                          >
                            Timesheets
                          </Link>
                        </div>
                      )}
                    </div>
                  )}

                  {hasPermission('warehouse_overview') && (
                    <div>
                      <button
                        onClick={() => toggleSection('warehouse')}
                        data-testid="admin-menu-warehouse"
                        className="w-full flex items-center justify-between px-4 py-3 text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 rounded-lg transition-colors font-medium"
                      >
                        <div className="flex items-center gap-3">
                          <Warehouse className="w-5 h-5" />
                          Warehouse Overview
                        </div>
                        {expandedSections.warehouse ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </button>
                      {expandedSections.warehouse && (
                        <div className="ml-8 mt-1 space-y-1">
                          <Link
                            to="/admin/warehouse/analytics"
                            onClick={() => setShowMenu(false)}
                            className={`w-full flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-colors ${
                              location.pathname === '/admin/warehouse/analytics'
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                            }`}
                          >
                            Analytics
                          </Link>
                          <Link
                            to="/admin/warehouse/timesheets"
                            onClick={() => setShowMenu(false)}
                            className={`w-full flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-colors ${
                              location.pathname === '/admin/warehouse/timesheets'
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                            }`}
                          >
                            Timesheets
                          </Link>
                        </div>
                      )}
                    </div>
                  )}

                  {hasPermission('health_safety') && (
                    <button
                      data-testid="admin-menu-health-safety"
                      className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 rounded-lg transition-colors font-medium"
                    >
                      <ShieldAlert className="w-5 h-5" />
                      Health & Safety
                    </button>
                  )}

                  {/* Finance - Collapsible */}
                  {hasPermission('finance_timesheets') && (
                    <div>
                      <button
                        data-testid="admin-menu-finance-toggle"
                        onClick={() => toggleSection('finance')}
                        className="w-full flex items-center justify-between px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors font-medium"
                      >
                        <div className="flex items-center gap-3">
                          <DollarSign className="w-5 h-5" />
                          Finance
                        </div>
                        {expandedSections.finance ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </button>
                      {expandedSections.finance && (
                        <div className="ml-8 mt-1 space-y-1">
                          <Link
                            to="/admin/warehouse-timesheets"
                            data-testid="admin-menu-warehouse-timesheets"
                            onClick={() => setShowMenu(false)}
                            className={`w-full flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-colors ${
                              location.pathname === '/admin/warehouse-timesheets'
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                            }`}
                          >
                            Warehouse Timesheets
                          </Link>
                          <Link
                            to="/admin/drivers-timesheets"
                            data-testid="admin-menu-drivers-timesheets-finance"
                            onClick={() => setShowMenu(false)}
                            className={`w-full flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-colors ${
                              location.pathname === '/admin/drivers-timesheets'
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                            }`}
                          >
                            Drivers Timesheets
                          </Link>
                          <Link
                            to="/admin/all-timesheets"
                            data-testid="admin-menu-all-timesheets"
                            onClick={() => setShowMenu(false)}
                            className={`w-full flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-colors ${
                              location.pathname === '/admin/all-timesheets'
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                            }`}
                          >
                            All Timesheets
                          </Link>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="my-3 border-t border-gray-200" />

                  {/* Company & HR - Collapsible */}
                  {(hasPermission('employees') || hasPermission('roadmap')) && (
                    <div>
                      <button
                        data-testid="admin-menu-company-hr-toggle"
                        onClick={() => toggleSection('companyHR')}
                        className="w-full flex items-center justify-between px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors font-medium"
                      >
                        <div className="flex items-center gap-3">
                          <Building2 className="w-5 h-5" />
                          Company & HR
                        </div>
                        {expandedSections.companyHR ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </button>
                      {expandedSections.companyHR && (
                        <div className="ml-8 mt-1 space-y-1">
                          {hasPermission('employees') && (
                            <Link
                              to="/admin/employees"
                              data-testid="admin-menu-employees"
                              onClick={() => setShowMenu(false)}
                              className={`w-full flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-colors ${
                                location.pathname === '/admin/employees'
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                              }`}
                            >
                              Employees
                            </Link>
                          )}
                          {/* Roadmap - Hidden on mobile per user request */}
                          {false && hasPermission('roadmap') && (
                            <Link
                              to="/admin/roadmap"
                              data-testid="admin-menu-roadmap"
                              onClick={() => setShowMenu(false)}
                              className={`w-full flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-colors ${
                                location.pathname === '/admin/roadmap'
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                              }`}
                            >
                              2026 Roadmap
                            </Link>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Impact - Collapsible */}
                  {(hasPermission('impact_stats') || hasPermission('impact_builder')) && (
                    <div>
                      <button
                        data-testid="admin-menu-impact-toggle"
                        onClick={() => toggleSection('impact')}
                        className="w-full flex items-center justify-between px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors font-medium"
                      >
                        <div className="flex items-center gap-3">
                          <TrendingUp className="w-5 h-5" />
                          Impact
                        </div>
                        {expandedSections.impact ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </button>
                      {expandedSections.impact && (
                        <div className="ml-8 mt-1 space-y-1">
                          {hasPermission('impact_stats') && (
                            <Link
                              to="/admin/impact/stats"
                              data-testid="admin-menu-impact-stats"
                              onClick={() => setShowMenu(false)}
                              className={`w-full flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-colors ${
                                location.pathname === '/admin/impact/stats'
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                              }`}
                            >
                              Update Impact Stats
                            </Link>
                          )}
                          {/* Impact Page Builder - Hidden on mobile per user request */}
                          {false && hasPermission('impact_builder') && (
                            <Link
                              to="/admin/impact/page"
                              data-testid="admin-menu-impact-page"
                              onClick={() => setShowMenu(false)}
                              className={`w-full flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-colors ${
                                location.pathname === '/admin/impact/page'
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                              }`}
                            >
                              Impact Page Builder
                            </Link>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Engagement - Collapsible */}
                  {(hasPermission('announcements') || hasPermission('newsletters') || hasPermission('surveys')) && (
                    <div>
                      <button
                        data-testid="admin-menu-engagement-toggle"
                        onClick={() => toggleSection('engagement')}
                        className="w-full flex items-center justify-between px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors font-medium"
                      >
                        <div className="flex items-center gap-3">
                          <TrendingUp className="w-5 h-5" />
                          Engagement
                        </div>
                        {expandedSections.engagement ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </button>
                      {expandedSections.engagement && (
                        <div className="ml-8 mt-1 space-y-1">
                          {hasPermission('announcements') && (
                            <Link
                              to="/admin/announcements"
                              data-testid="admin-menu-announcements"
                              onClick={() => setShowMenu(false)}
                              className={`w-full flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-colors ${
                                location.pathname === '/admin/announcements'
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                              }`}
                            >
                              Announcements
                            </Link>
                          )}
                          {hasPermission('newsletters') && (
                            <Link
                              to="/admin/newsletters"
                              data-testid="admin-menu-newsletters"
                              onClick={() => setShowMenu(false)}
                              className={`w-full flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-colors ${
                                location.pathname === '/admin/newsletters'
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                              }`}
                            >
                              Newsletters
                            </Link>
                          )}
                          {hasPermission('surveys') && (
                            <Link
                              to="/admin/surveys"
                              data-testid="admin-menu-surveys"
                              onClick={() => setShowMenu(false)}
                              className={`w-full flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-colors ${
                                location.pathname === '/admin/surveys'
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                              }`}
                            >
                              Surveys
                            </Link>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Admin - Collapsible */}
                  {hasPermission('settings') && (
                    <div>
                      <button
                        data-testid="admin-menu-admin-toggle"
                        onClick={() => toggleSection('admin')}
                        className="w-full flex items-center justify-between px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors font-medium"
                      >
                        <div className="flex items-center gap-3">
                          <Settings className="w-5 h-5" />
                          Admin
                        </div>
                        {expandedSections.admin ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </button>
                      {expandedSections.admin && (
                        <div className="ml-8 mt-1 space-y-1">
                          <Link
                            to="/admin/settings/absence-types"
                            data-testid="admin-menu-settings"
                            onClick={() => setShowMenu(false)}
                            className={`w-full flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-colors ${
                              location.pathname.startsWith('/admin/settings')
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                            }`}
                          >
                            Settings
                          </Link>
                          <Link
                            to="/admin/notification-settings"
                            data-testid="admin-menu-notifications"
                            onClick={() => setShowMenu(false)}
                            className={`w-full flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-colors ${
                              location.pathname === '/admin/notification-settings'
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                            }`}
                          >
                            Notifications
                          </Link>
                        </div>
                      )}
                    </div>
                  )}
                </nav>
              </div>

              {/* Bottom Menu Items - User Profile */}
              <div className="border-t border-gray-200 p-3">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center text-white font-semibold text-base flex-shrink-0 overflow-hidden">
                    {user?.profile_image ? (
                      <img 
                        src={user.profile_image} 
                        alt={`${user.first_name} ${user.last_name}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span>{user?.first_name?.[0]}{user?.last_name?.[0]}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate leading-tight">
                      {user?.first_name} {user?.last_name}
                    </p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <p className="text-xs text-gray-500 truncate">
                        {user?.job_title || 'No Job Title'}
                      </p>
                      <span className="text-gray-300 text-xs">|</span>
                      <button
                        data-testid="admin-menu-logout"
                        onClick={() => {
                          setShowMenu(false);
                          onLogout();
                        }}
                        className="text-xs text-gray-500 hover:text-red-600 transition-colors flex items-center gap-1"
                        title="Logout"
                      >
                        <LogOut className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

        {/* Main Content */}
        <div className="px-6 py-6 sm:py-8">
          {children}
        </div>
      </div>

      {/* PWA Install Prompt for first-time users */}
      <InstallPrompt 
        isInstallable={isInstallable}
        onInstall={promptInstall}
      />
    </div>
  );
};

export default AdminLayout;
